import {
  Avatar,
  Badge,
  Box,
  Card,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  Text,
  Title,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core'
import Userfront from '@userfront/core'

import { useAuth } from '@repo/common/queries/auth/userfront_auth'

export function ProfileCard() {
  const theme = useMantineTheme()
  const { data: user, isLoading } = useAuth()
  const { colorScheme } = useMantineColorScheme()
  const isDark = colorScheme === 'dark'

  if (isLoading) {
    return (
      <Card
        shadow="sm"
        radius="xs"
        withBorder
        p="xl"
        style={{ position: 'relative', minHeight: '200px' }}
      >
        <LoadingOverlay visible={true} />
      </Card>
    )
  }

  if (!user?.is_authenticated) {
    return (
      <Card shadow="sm" radius="xs" withBorder p="xl">
        <Text ta="center">Please log in to view your profile information.</Text>
      </Card>
    )
  }

  // Generate avatar initials from email address
  const initials = user.email[0]!.toUpperCase()

  // Format roles for display
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const roles = Userfront.user?.hasRole
    ? ['admin', 'support', 'member', 'user'].filter((role) =>
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        Userfront.user?.hasRole?.(role),
      )
    : []

  // Check if account is verified
  const isVerified = user.is_email_confirmed

  return (
    <Card shadow="sm" radius="xs" withBorder p={0}>
      {/* Header section with avatar and name */}
      <Box
        p="xl"
        style={{
          background: isDark
            ? `linear-gradient(to right, var(--mantine-color-dark-7), var(--mantine-color-dark-5))`
            : `linear-gradient(to right, var(--mantine-color-gray-0), var(--mantine-color-gray-1))`,
          borderBottom: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
        }}
        // style={{
        //   background: `linear-gradient(45deg, ${theme.colors.blue[6]} 0%, ${theme.colors.indigo[9]} 100%)`,
        // }}
      >
        <Group justify="center" gap="xl">
          <Avatar
            size={100}
            radius={100}
            color="white"
            style={{
              border: `4px solid white`,
              backgroundColor: theme.colors.blue![8],
            }}
          >
            <Text size="xl" fw={700}>
              {initials}
            </Text>
          </Avatar>

          <div>
            <Text c="white" opacity={0.9}>
              {user.email}
            </Text>
            {isVerified ? (
              <Badge color="green" mt={5}>
                Verified Account
              </Badge>
            ) : (
              <Badge color="yellow" mt={5}>
                Email Not Verified
              </Badge>
            )}
          </div>
        </Group>
      </Box>

      {/* Account details */}
      <Box p="xl">
        <Title order={4} mb="md">
          Account Information
        </Title>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper p="md" radius="xs" withBorder>
              <Text size="sm" c="dimmed" fw={500}>
                USER ID
              </Text>
              <Text size="sm" mt={5}>
                {user.uuid}
              </Text>
            </Paper>
          </Grid.Col>

          {/*<Grid.Col span={{ base: 12, md: 6 }}>*/}
          {/*  <Paper p="md" radius="xs" withBorder>*/}
          {/*    <Text size="sm" c="dimmed" fw={500}>ACCOUNT STATUS</Text>*/}
          {/*    <Group mt={5} gap="xs">*/}
          {/*      {isVerified ? (*/}
          {/*        <Badge color="green">Verified</Badge>*/}
          {/*      ) : (*/}
          {/*        <Badge color="yellow">Pending Verification</Badge>*/}
          {/*      )}*/}
          {/*    </Group>*/}
          {/*  </Paper>*/}
          {/*</Grid.Col>*/}

          <Grid.Col span={12}>
            <Paper p="md" radius="xs" withBorder>
              <Text size="sm" c="dimmed" fw={500}>
                ROLES & PERMISSIONS
              </Text>
              <Group mt={5} gap="xs">
                {roles.length > 0 ? (
                  roles.map((role) => (
                    <Badge key={role} color={getRoleBadgeColor(role)}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </Badge>
                  ))
                ) : (
                  <Text size="sm" c="dimmed">
                    No roles assigned
                  </Text>
                )}
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>
      </Box>
    </Card>
  )
}

// Helper function to get badge color based on role
function getRoleBadgeColor(role: string): string {
  switch (role.toLowerCase()) {
    case 'admin':
      return 'red'
    case 'support':
      return 'indigo'
    case 'member':
      return 'blue'
    default:
      return 'gray'
  }
}

export default ProfileCard
